import InsightLayout from "../../components/InsightLayout";
import FoerderHinweis from "../../components/FoerderHinweis";
import BewertungsHinweis from "../../components/BewertungsHinweis";

const SUBTITLE = `Bevor Sie einen Euro in KI investieren, beantworten Sie sieben Fragen. Sie dauern zehn Minuten und ersparen Ihnen potenziell ein sechsstelliges Lehrgeld. Aus zwölf Mandaten habe ich gelernt: Wer alle sieben sauber beantworten kann, gehört zu den 20 % der KI-Projekte, die produktiv gehen. Wer bei mehr als zwei Fragen ins Stocken gerät, ist noch nicht bereit — und das ist eine wertvolle, keine schlechte Erkenntnis.`;

const KiReadinessCheck = () => {
  return (
    <InsightLayout
      title="Der KI-Readiness-Check: 7 Fragen, die Sie vor jeder KI-Investition beantworten müssen"
      subtitle={SUBTITLE}
      metaDescription="Sieben Fragen vor jeder KI-Investition, aus zwölf Mandaten verdichtet. Zehn Minuten Aufwand, inklusive Auswertung und den typischen Stolperstellen."
      publishedDate="2026-05-26"
      readingTime="9 Min Lesezeit"
      category="KI-Strategie"
    >
      <p className="text-lg">
        Die meisten KI-Investitionen scheitern nicht an der Technik. Sie
        scheitern daran, dass niemand vorher die richtigen Fragen gestellt hat.
        Ich habe in zwölf Mandaten gesehen, wie Projekte mit sechsstelligen
        Budgets gegen die Wand fuhren — und fast immer hätte ein ehrlicher
        Vorab-Check das Scheitern vorhergesagt.
      </p>

      <p>
        Hier ist dieser Check. Sieben Fragen, zehn Minuten. Beantworten Sie
        jede ehrlich. Die Auswertung am Ende sagt Ihnen, ob Sie loslegen
        sollten — oder zuerst Hausaufgaben machen müssen.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 1: Welche Top-3-Kennzahl soll sich bewegen — und um wie viel?
      </h2>
      <p>
        Nicht „wir wollen effizienter werden". Sondern: <em>„Die Bearbeitungs-
        zeit pro Angebot soll von 4 Stunden auf 1 Stunde sinken"</em> oder{" "}
        <em>„die Conversion-Rate im Erstkontakt soll von 18 % auf 25 %
        steigen"</em>. Eine Zahl, ein Vorher, ein Ziel-Nachher.
      </p>
      <p>
        <strong>Wenn Sie diese Frage nicht in einem Satz mit einer Zahl
        beantworten können, ist das Projekt noch keine Investition, sondern
        ein Experiment.</strong> Das ist okay — dann budgetieren Sie es auch
        als Experiment (klein, zeitlich begrenzt), nicht als strategische
        Initiative.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 2: Existieren die nötigen Daten — und in welchem Zustand?
      </h2>
      <p>
        KI braucht Daten. Die entscheidende Frage ist nicht „haben wir Daten"
        (die hat jeder), sondern: <em>Liegen die relevanten Daten strukturiert,
        zugänglich und aktuell vor — oder verteilt über fünf Systeme, drei
        Excel-Inseln und den Kopf eines Mitarbeiters, der nächstes Jahr in
        Rente geht?</em>
      </p>
      <p>
        In jedem zweiten Mandat war die ehrliche Antwort: „verteilt und
        chaotisch". Das ist kein K.o.-Kriterium — aber es bedeutet, dass der
        erste Teil des Projekts Daten-Aufräumen ist, nicht KI. Wer das
        einplant, gewinnt. Wer es übersieht, läuft im Monat sechs in die Wand.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 3: Verarbeitet die Lösung personenbezogene oder sensible Daten?
      </h2>
      <p>
        Wenn ja, kommen Datenschutz-Pflichten ins Spiel, die das Projekt
        verteuern und verlangsamen — und die Sie früh kennen müssen, nicht
        kurz vor dem Go-live. Konkret: Wenn KI Entscheidungen über Menschen
        beeinflusst (Bewerber, Kunden, Mitarbeiter), brauchen Sie
        wahrscheinlich eine Datenschutz-Folgenabschätzung, und je nach
        Anwendung greift auch der EU AI Act.
      </p>
      <p>
        Faustregel: Personenbezug + Entscheidungsrelevanz = rechtliche
        Vorarbeit. Das ist keine Bürokratie um ihrer selbst willen, sondern
        Risikomanagement. Wer es ignoriert, riskiert Bußgelder, die das
        gesamte Projekt-ROI auffressen.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 4: Wer im Unternehmen besitzt das Projekt — mit Mandat und Zeit?
      </h2>
      <p>
        Nicht „die IT macht das mit". Sondern: eine namentlich benannte Person
        mit (a) Entscheidungsbefugnis, (b) echter Zeit dafür und (c) genug
        Verständnis für den Fachprozess UND die Technik, um zwischen beiden zu
        übersetzen. Diese Rolle entscheidet über Erfolg oder Misserfolg.
      </p>
      <p>
        Das häufigste Scheitermuster: Das Projekt gehört „allen" — also
        niemandem. Es läuft nebenher, niemand priorisiert es, nach vier
        Monaten ist die Energie weg. <strong>Wenn Sie keine Person benennen
        können, die das Projekt mit Mandat und Zeit besitzt, starten Sie
        nicht.</strong>
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 5: Sind die Mitarbeiter eingebunden — oder werden sie überrascht?
      </h2>
      <p>
        KI, die Arbeitsabläufe verändert, ist im DACH-Raum oft
        mitbestimmungspflichtig — der Betriebsrat muss früh mit ins Boot.
        Aber das ist nur die formale Seite. Die wichtigere: Die Menschen, deren
        Arbeit die KI berührt, müssen verstehen, dass sie unterstützt und nicht
        ersetzt werden — sonst sabotieren sie das Projekt, bewusst oder
        unbewusst.
      </p>
      <p>
        Ich habe Projekte gesehen, die technisch funktionierten und trotzdem
        scheiterten, weil die Belegschaft sie nicht angenommen hat. Change-
        Management ist kein Nachgedanke, sondern Teil der Investition. Planen
        Sie es von Anfang an ein.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 6: Akzeptieren Sie eine Human-in-the-Loop-Phase?
      </h2>
      <p>
        Keine seriöse KI-Lösung läuft ab Tag eins vollautomatisch fehlerfrei.
        Die ersten 6 bis 12 Monate braucht es einen Menschen, der prüft,
        korrigiert und das System justiert. Wer Vollautomatisierung ab Woche
        eins erwartet, wird enttäuscht — und wer sie verspricht, verkauft
        Risiko.
      </p>
      <p>
        Die richtige Erwartung: Die KI macht die Vorarbeit, ein Mensch
        validiert, das System lernt aus den Korrekturen. Über die Zeit sinkt
        der menschliche Aufwand. Wenn Sie diese Phase nicht akzeptieren
        wollen, ist Ihr Unternehmen noch nicht bereit für produktive KI.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Frage 7: Wer betreibt die Lösung in zwei Jahren — ohne den Anbieter?
      </h2>
      <p>
        Externe Berater und Dienstleister implementieren, dann gehen sie.
        Was bleibt, ist ein System, das jemand verstehen und weiterentwickeln
        muss. Wenn niemand intern dazu in der Lage ist, haben Sie nicht eine
        KI-Lösung gekauft, sondern eine Abhängigkeit.
      </p>
      <p>
        Mein Standard-Rat: Planen Sie 20 bis 30 Prozent des Projektbudgets für
        internen Wissensaufbau ein. Das wird fast immer zuerst gestrichen — und
        fast immer später bereut. Wer interne Kompetenz aufbaut, wird mit jedem
        weiteren KI-Projekt schneller und günstiger.
      </p>

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Die Auswertung
      </h2>

      <div className="bg-gray-50 rounded-xl p-6 my-6">
        <p className="mb-4">
          <strong>7 von 7 klar beantwortet:</strong> Sie gehören zur Minderheit,
          die bereit ist. Starten Sie — aber halten Sie an den Antworten fest,
          besonders an Frage 1 (die Zahl) als Erfolgsmaßstab.
        </p>
        <p className="mb-4">
          <strong>5–6 beantwortet:</strong> Grundsätzlich bereit, aber mit
          Lücken. Schließen Sie die offenen Punkte, bevor das Budget freigegeben
          wird — meist sind es Frage 2 (Daten) oder Frage 4 (Ownership).
        </p>
        <p className="mb-4">
          <strong>3–4 beantwortet:</strong> Noch nicht bereit. Das ist kein
          Scheitern, sondern eine ehrliche Standortbestimmung. Arbeiten Sie
          zuerst die Grundlagen ab — sonst wird die KI-Investition zum
          Lehrgeld.
        </p>
        <p>
          <strong>Unter 3:</strong> Investieren Sie das geplante KI-Budget
          erst einmal in Datenstruktur, klare Verantwortlichkeiten und internes
          Verständnis. Die KI kommt danach — und funktioniert dann.
        </p>
      </div>

      <BewertungsHinweis
        text="Dieselbe Prozessreife, die KI-Projekte gelingen lässt, hebt auch Ihren Unternehmenswert. Käufer zahlen Aufschläge für dokumentierte Prozesse und ein Geschäft, das ohne den Inhaber läuft. Der kostenlose Valuate-Check zeigt, wo Ihr Branchen-Multiple steht und welche Werttreiber es bewegen."
        links={[
          { href: "https://valuate.daaniyalkhan.com/branchen/", label: "Multiples nach Branche" },
          { href: "https://valuate.daaniyalkhan.com/wissen/werttreiber-sellability/", label: "Die 8 Werttreiber" },
          { href: "https://valuate.daaniyalkhan.com/", label: "Kostenloser Bewertungs-Check" },
        ]}
      />

      <h2 className="text-3xl font-bold text-[#0F1B2D] mt-12 mb-4 font-playfair">
        Warum diese sieben Fragen mehr wert sind als jedes Tool-Vergleich
      </h2>

      <p>
        Es gibt unzählige Artikel darüber, welches KI-Tool das beste ist (ich
        habe selbst{" "}
        <a
          href="/insights/llm-tool-landschaft-2026"
          className="text-[#C5A66A] underline"
        >
          einen geschrieben
        </a>
        ). Aber die Tool-Wahl ist die letzte Frage, nicht die erste. Die
        sieben Fragen oben entscheiden über Erfolg oder Misserfolg, lange bevor
        ein einziges Tool ausgewählt wird.
      </p>

      <p>
        Das Muster aus zwölf Mandaten ist eindeutig: Die Unternehmen, die diese
        Fragen vorab ehrlich beantworten, landen im erfolgreichen Fünftel. Die
        anderen produzieren teure Pilotprojekte, die nie produktiv gehen — und
        schieben es hinterher auf „die Technik war noch nicht reif". War sie
        meistens. Reif war nur die Vorbereitung nicht.
      </p>

      <p>
        Der ehrlichste Rat, den ich einem Mittelständler geben kann, lautet
        deshalb oft nicht „kaufen Sie diese KI", sondern „beantworten Sie zuerst
        diese sieben Fragen". Wer das tut, braucht mich für den Rest meist gar
        nicht mehr so dringend — und genau das ist das Zeichen einer ehrlichen
        Beratung.
      </p>

      <p className="mt-12 text-sm text-gray-600 italic">
        Dieser Check ist eine Verdichtung wiederkehrender Muster aus
        Beratungsmandaten, keine Rechts- oder Investitionsberatung. Für die
        konkrete Bewertung Ihres Vorhabens — inklusive der rechtlichen Punkte
        aus Frage 3 — ziehen Sie qualifizierten Rat hinzu. Vertiefend:{" "}
        <a
          href="/insights/ki-adoption-mittelstand"
          className="text-[#C5A66A] underline"
        >
          Warum 80 % der KI-Pilotprojekte scheitern
        </a>{" "}
        ·{" "}
        <a
          href="/insights/eu-ai-act-2026"
          className="text-[#C5A66A] underline"
        >
          EU AI Act 2026
        </a>{" "}
        ·{" "}
        <a
          href="/insights/ki-orchestrierte-angriffe"
          className="text-[#C5A66A] underline"
        >
          KI-orchestrierte Angriffe
        </a>
        .
      </p>

      <FoerderHinweis
        text="Zur Readiness gehört auch die Finanzierungsseite: Digitalisierungs- und KI-Vorhaben werden von Bund und Ländern bezuschusst, im Mittelstand je nach Programm mit 30 bis 80 % der förderfähigen Kosten. Die Prüfung kostet 60 Sekunden und gehört vor die Investitionsentscheidung — nicht danach."
        links={[
          { href: "https://foerdermittel.daaniyalkhan.com/", label: "Kostenloser Förder-Check" },
          { href: "https://foerdermittel.daaniyalkhan.com/programme/", label: "Förderprogramme nach Bundesland" },
          { href: "https://foerdermittel.daaniyalkhan.com/wissen/foerderantrag-fehler/", label: "Die 7 häufigsten Antragsfehler" },
        ]}
      />
    </InsightLayout>
  );
};

export default KiReadinessCheck;
